import type { BigSearchPersonEvent } from "../../api/big-search";
import {
  FETCH_CONTRACTOR_SHOWS_REQUEST,
  FETCH_CONTRACTOR_SHOWS_SUCCESS,
  FETCH_CONTRACTOR_SHOWS_FAILURE,
} from "../constants/contractorShows";

export interface ContractorShowsEntry {
  results: BigSearchPersonEvent[];
  totalCount: number;
  /** The request could not be answered (403, deleted person). Card stays silent. */
  failed?: boolean;
}

export interface ContractorShowsState {
  byContractorId: Record<string, ContractorShowsEntry>;
  loading: Record<string, boolean>;
}

interface ContractorShowsAction {
  type: string;
  payload?: {
    contractorId?: string;
    data?: ContractorShowsEntry;
    error?: unknown;
  };
}

/**
 * Cache of "shows this person worked", keyed by contractor id.
 *
 * The cache is what makes the feature affordable at all: the contact card is a
 * TOOLTIP, so it unmounts the moment the pointer leaves. Without a store the
 * same person would refetch on every hover, and an admin scanning a list hovers
 * a lot. Mirrors `contractorRatings`, which solved the same problem.
 *
 * An entry is written even on failure (as an empty list) so the caller's
 * "already fetched?" guard flips and a structural 403 is not retried forever.
 */
export const initialState: ContractorShowsState = {
  // { [contractorId]: { results: [...], totalCount: number } }
  byContractorId: {},
  // { [contractorId]: true|false }
  loading: {},
};

const contractorShowsReducer = (
  state: ContractorShowsState = initialState,
  action: ContractorShowsAction,
): ContractorShowsState => {
  switch (action.type) {
    case FETCH_CONTRACTOR_SHOWS_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          [String(action.payload?.contractorId)]: true,
        },
      };

    case FETCH_CONTRACTOR_SHOWS_SUCCESS: {
      const contractorId = String(action.payload?.contractorId);
      const data = action.payload?.data ?? { results: [], totalCount: 0 };
      return {
        ...state,
        loading: { ...state.loading, [String(contractorId)]: false },
        byContractorId: {
          ...state.byContractorId,
          [String(contractorId)]: data,
        },
      };
    }

    case FETCH_CONTRACTOR_SHOWS_FAILURE: {
      const id = String(action.payload?.contractorId);
      return {
        ...state,
        loading: { ...state.loading, [id]: false },
        byContractorId: {
          ...state.byContractorId,
          [id]: { results: [], totalCount: 0, failed: true },
        },
      };
    }

    default:
      return state;
  }
};

export default contractorShowsReducer;
